/*
 * Gala Parfemi — Kaching cart drawer checkout buttons (card + COD)
 * ---------------------------------------------------------------------------
 * The cart drawer is rendered by the Kaching Cart app (a custom web component),
 * so the theme can't template these buttons in Liquid — we inject them with JS
 * by cloning Kaching's own checkout button (`#kaching-cart__checkout-button`).
 *
 * We REPLACE Kaching's single "Idi na plaćanje" button with TWO owned buttons:
 *
 *   1. CARD (primary, "Plati karticom –10%"): writes cart attribute
 *      `payment-method: card` via /cart/update.js, THEN navigates to the
 *      Shopify checkout. A Shopify Function / automatic discount reads that
 *      attribute and applies 10% off. Shopify Payments shows normally.
 *
 *   2. COD (secondary, "Plaćanje pouzećem"): opens the Releasit COD order form
 *      as a MODAL, populated with the WHOLE cart — NO cart attribute, no
 *      discount. Releasit creates the order via its own app proxy, bypassing
 *      Shopify checkout entirely. Only shown when the Releasit app is available.
 *
 * Why owned clones instead of Kaching's `setCheckoutButtonClickHandler`:
 *   Kaching navigates to checkout WITHOUT awaiting async click handlers, and
 *   its navigation can't be cancelled from a click listener. So an async
 *   /cart/update.js write always loses the race. We clone Kaching's button for
 *   pixel-perfect styling, strip its hooks (`name`, `id`, `data-kaching-checkout`,
 *   `form`, and `data-ocu-checkout`) so neither Kaching NOR Zipify OCU hijack
 *   the click, and drive the click ourselves.
 *
 * The `data-ocu-checkout` strip is CRITICAL: Zipify OneClickUpsell binds a
 * CAPTURE-phase document listener on `[data-ocu-checkout]` that redirects to
 * checkout before any bubble-phase handler runs. Left in place it would fire
 * before our attribute write / modal open. Tradeoff: no OCU pre-purchase upsell
 * on these buttons.
 */
(function () {
  if (window.__galaKachingCheckoutInit) return;
  window.__galaKachingCheckoutInit = true;

  // --- Config -------------------------------------------------------------
  var CARD_LABEL = 'Plati karticom –10%'; // “Pay by card –10%”
  var COD_LABEL = 'Plaćanje pouzećem'; // “Cash on delivery”
  var CARD_BTN_ID = 'gala-card-checkout-button';
  var COD_BTN_ID = 'gala-cod-checkout-button';
  var ATTR_KEY = 'payment-method';
  var CARD_VALUE = 'card';
  var ORIGINAL_SELECTOR =
    '#kaching-cart__checkout-button, :is(button,input)[name="checkout"]';
  var LOADING_CLASS = 'kaching-cart__checkout-button--loading';
  // sessionStorage flag set when the shopper commits to a card checkout, so we
  // can drop the `payment-method` attribute the moment they return from the
  // Shopify checkout page — see clearOnReturnFromCheckout(). Survives the
  // cross-origin round trip because it's scoped to this tab + our origin.
  var CARD_ARMED_KEY = 'gala-card-armed';

  // Pristine clone of Kaching's checkout button, captured on each render so we
  // can (re)build our clones even after the original has been replaced (e.g.
  // when Releasit loads late and we need to add the COD button on its own).
  var btnTemplate = null;

  // --- Serialized cart writes --------------------------------------------
  // All attribute writes share one promise chain, so ordering is guaranteed
  // (the load-time reset can never land after a button click's write).
  var writeChain = Promise.resolve();
  function setPaymentMethod(value) {
    writeChain = writeChain
      .then(function () {
        var attrs = {};
        attrs[ATTR_KEY] = value;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs }),
        });
      })
      .catch(function () {});
    return writeChain;
  }

  // --- Checkout URL (prefer Kaching's, which carries market/locale) -------
  function getCheckoutUrl() {
    try {
      if (window.kachingCartApi && kachingCartApi.getCartData) {
        return Promise.resolve(kachingCartApi.getCartData())
          .then(function (d) {
            return (d && d.checkoutUrl) || '/checkout';
          })
          .catch(function () {
            return '/checkout';
          });
      }
    } catch (e) {}
    return Promise.resolve('/checkout');
  }

  // --- Releasit internal store access ------------------------------------
  function rsi() {
    return window._rsiV2 || null;
  }
  // True once Releasit's store is ready and exposes what we need.
  function rsiReady() {
    var r = rsi();
    return !!(
      r &&
      typeof r.getState === 'function' &&
      typeof r.setState === 'function' &&
      r.cartService &&
      typeof r.cartService.clearCart === 'function' &&
      typeof r.cartService.addProduct === 'function'
    );
  }

  // Resolve the id of Releasit's Storefront cart, creating one if needed.
  function getStorefrontCartId() {
    var r = rsi();
    var st = r.getState();
    var id = st && st.cartInfo && st.cartInfo.id;
    if (id) return Promise.resolve(id);
    try {
      return Promise.resolve(r.cartService.createCart()).then(function (res) {
        var cart =
          (res && res.cartCreate && res.cartCreate.cart) ||
          (res && res.cart) ||
          res;
        return cart && cart.id ? cart.id : null;
      });
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  // --- Open the COD form with the current cart ---------------------------
  // Replicates Releasit's `refreshCartFromCartJs(cartId, setState)`: sync the
  // Ajax cart into Releasit's separate Storefront cart, then open the modal.
  function openCodForm() {
    var r = rsi();
    var CS = r.cartService;
    return getStorefrontCartId().then(function (cartId) {
      if (!cartId) throw new Error('No Releasit Storefront cart id');
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.json();
        })
        .then(function (ajaxCart) {
          var lines = (ajaxCart.items || []).map(function (it) {
            var line = {
              merchandiseId: 'gid://shopify/ProductVariant/' + it.variant_id,
              quantity: it.quantity || 1,
            };
            if (it.properties && Object.keys(it.properties).length) {
              line.attributes = Object.keys(it.properties).map(function (k) {
                return { key: k, value: String(it.properties[k]) };
              });
            }
            return line;
          });

          // Rebuild Releasit's Storefront cart to match the Ajax cart exactly.
          return CS.clearCart(cartId)
            .then(function () {
              if (!lines.length) return null;
              return CS.addProduct(cartId, lines);
            })
            .then(function (addRes) {
              var sfCart =
                (addRes && addRes.cartLinesAdd && addRes.cartLinesAdd.cart) ||
                (addRes && addRes.cart) ||
                null;
              var products =
                sfCart && sfCart.lines
                  ? sfCart.lines.edges.map(function (e) {
                      return e.node.merchandise;
                    })
                  : [];
              r.setState({
                cartInfo: sfCart || undefined,
                products: products,
                triggerCartUpdate: 0,
                isOpen: true,
                isCartButtonLoading: false,
                isClearingCart: false,
              });
            });
        });
    });
  }

  // --- Busy state helpers -------------------------------------------------
  function setSpinner(btn, show) {
    if (!btn) return;
    var spinner = btn.querySelector('.kaching-cart__checkout-button-spinner');
    if (spinner) spinner.style.display = show ? '' : 'none';
  }
  // Put an owned button into the loading state while its click action runs.
  function setBusy(btn) {
    if (!btn) return;
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add(LOADING_CLASS);
    btn.disabled = true;
    setSpinner(btn, true);
  }
  // Force an owned button back to a clickable state. Needed because:
  //   1. Kaching renders its checkout button DISABLED + `--loading` while it
  //      fetches cart/checkout data, so a fresh clone inherits that frozen
  //      state — and Kaching only ever re-enables its OWN button (by id).
  //   2. On bfcache restore (Back button) after the card action set busy.
  function clearBusy(btn) {
    if (!btn) return;
    btn.removeAttribute('aria-busy');
    btn.classList.remove(LOADING_CLASS);
    btn.disabled = false;
    btn.removeAttribute('disabled');
    setSpinner(btn, false);
  }
  function resetButtons() {
    clearBusy(document.getElementById(CARD_BTN_ID));
    clearBusy(document.getElementById(COD_BTN_ID));
  }

  // --- Click actions ------------------------------------------------------
  // Card: write the attribute, THEN navigate. Stays busy until navigation.
  function proceedCard(btn) {
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    setBusy(btn);
    // Remember we armed a card checkout so we can drop the attribute the instant
    // the shopper comes back from checkout (no full page reload needed).
    try {
      sessionStorage.setItem(CARD_ARMED_KEY, '1');
    } catch (e) {}
    setPaymentMethod(CARD_VALUE)
      .then(getCheckoutUrl)
      .then(function (url) {
        window.location.href = url;
      })
      .catch(function () {
        window.location.href = '/checkout';
      });
  }
  // COD: open the Releasit modal (does NOT navigate), then release the button.
  function openCod(btn) {
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    if (!rsiReady()) return; // Releasit not loaded — do nothing.
    setBusy(btn);
    openCodForm()
      .catch(function (e) {
        if (window.console) console.warn('[gala-cod] open failed', e);
      })
      .then(function () {
        clearBusy(btn);
      });
  }

  // --- Build an owned button by cloning Kaching's (keeps styling) ---------
  function buildButton(opts) {
    var b = btnTemplate.cloneNode(true);
    b.id = opts.id;
    // Strip Kaching's hooks (so its delegated checkout handler ignores the
    // node) and Zipify OCU's hook (so its capture-phase redirect never fires).
    b.removeAttribute('name');
    b.removeAttribute('data-kaching-checkout');
    b.removeAttribute('data-ocu-checkout');
    b.removeAttribute('form');
    if (opts.extraClass) b.classList.add(opts.extraClass);
    b.setAttribute('aria-label', opts.label);

    var label = b.querySelector('.kaching-cart__checkout-button-label');
    if (label) label.textContent = opts.label;
    else b.textContent = opts.label;

    // The clone may inherit Kaching's initial disabled/`--loading` state; force
    // it clickable since we (not Kaching) own this button's lifecycle.
    clearBusy(b);

    b.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      opts.onClick(b);
    });
    return b;
  }

  function removeById(id) {
    var el = document.getElementById(id);
    if (el) el.remove();
  }

  // --- Injection ----------------------------------------------------------
  // On each Kaching render, replace the original with our card button; add the
  // COD button below it whenever the Releasit app is ready.
  function inject() {
    var original = document.querySelector(ORIGINAL_SELECTOR);
    if (original) {
      btnTemplate = original.cloneNode(true); // pristine snapshot for clones
      removeById(CARD_BTN_ID);
      removeById(COD_BTN_ID);
      var cardBtn = buildButton({
        id: CARD_BTN_ID,
        label: CARD_LABEL,
        extraClass: 'gala-card-checkout-button',
        onClick: proceedCard,
      });
      original.replaceWith(cardBtn); // remove Kaching's original (with its hooks)
    }

    var card = document.getElementById(CARD_BTN_ID);
    if (!card || !btnTemplate) return; // drawer closed / nothing rendered yet

    // The COD button exists iff the Releasit app is available.
    var cod = document.getElementById(COD_BTN_ID);
    if (rsiReady()) {
      if (!cod) {
        cod = buildButton({
          id: COD_BTN_ID,
          label: COD_LABEL,
          extraClass: 'gala-cod-checkout-button',
          onClick: openCod,
        });
        card.parentNode.insertBefore(cod, card.nextSibling); // COD below card
      }
    } else if (cod) {
      cod.remove();
    }
  }

  // Re-inject whenever Kaching re-renders the drawer (debounced with rAF).
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      inject();
    });
  }

  // --- Keep non-card checkout paths clean --------------------------------
  // `payment-method: card` is only meant to live for the single hop from the
  // card button to checkout. The checkout page does NOT run this script, so if
  // this script runs (page load / bfcache restore) and the attribute is still
  // set, it's leftover from a card checkout the shopper backed out of. Drop it
  // so a later COD order can't inherit a stale `card`.
  function clearStalePaymentMethod() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (cart && cart.attributes && cart.attributes[ATTR_KEY]) {
          setPaymentMethod('');
        }
      })
      .catch(function () {});
  }

  // Drop the stale `payment-method` when the shopper RETURNS to the storefront
  // from the checkout page — via background AJAX, WITHOUT reloading the page.
  // Covers the case the pageshow/load handlers miss: the page stayed alive
  // (e.g. checkout opened in another tab) so we clear on tab refocus. Gated on
  // the armed flag so ordinary tab switches don't poll /cart.js.
  function clearOnReturnFromCheckout() {
    var armed = false;
    try {
      armed = sessionStorage.getItem(CARD_ARMED_KEY) === '1';
    } catch (e) {}
    if (!armed) return;
    try {
      sessionStorage.removeItem(CARD_ARMED_KEY);
    } catch (e) {}
    clearStalePaymentMethod();
    resetButtons();
  }

  // Releasit's app embed loads async; poll briefly so the COD button appears as
  // soon as the store is ready even without a drawer re-render.
  function waitForRsi() {
    if (rsiReady()) {
      schedule();
      return;
    }
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (rsiReady()) {
        clearInterval(timer);
        schedule();
      } else if (tries > 40) {
        clearInterval(timer); // ~12s — give up (app not installed)
      }
    }, 300);
  }

  function start() {
    inject();
    // A fresh page load (incl. a full reload when returning from checkout)
    // already drops any stale attribute here; consume the armed flag too.
    clearStalePaymentMethod();
    try {
      sessionStorage.removeItem(CARD_ARMED_KEY);
    } catch (e) {}
    waitForRsi();
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
    });
    // Returning to the storefront from checkout WITHOUT a full reload — clear
    // the stale attribute in the background (no page refresh):
    //   - bfcache restore (Back button): pageshow does NOT re-run this script,
    //     so clear here, and reset the frozen loading state proceedCard() left
    //     on the buttons so they're clickable again.
    //   - tab refocus (checkout in another tab): visibilitychange.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        try {
          sessionStorage.removeItem(CARD_ARMED_KEY);
        } catch (e2) {}
        clearStalePaymentMethod();
        resetButtons();
      }
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') clearOnReturnFromCheckout();
    });
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
